import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { action, observable, runInAction } from 'mobx';
import type { SortingObservable } from '../../components/Grid/Grid';
import { GridColDef } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import { AUDIT_PATH } from '../../store/constants';
import type { AuditEvent } from '../../types/practice-types';
import { getPathWithQuery } from '../../utils/url-utils';
import { expandCollapseRenderer, SuccessRenderer } from './renderers';

dayjs.extend(utc);

const initialPaging = { totalEventsCount: 0, maxPagesToDisplay: 10, pageSize: 25, offset: 0 };

const initialSort: SortingObservable<AuditEvent> = { field: 'id', order: 'DESC' };

export default class AuditStore extends AbstractStore {
  @observable loadingEvents = true;

  @observable events: AuditEvent[] = [];

  @observable paging = { ...initialPaging };

  @observable pageNumber = 0;

  @observable searchText = '';

  @observable minAuditId = '';

  @observable isFailuresOnly = false;

  @observable selectedTypes: string[] = [];

  @observable expandedRowIds: string[] = [];

  public sorting: SortingObservable<AuditEvent> = { ...initialSort };

  public get colDefs(): GridColDef<AuditEvent>[] {
    return [
      {
        headerName: '',
        field: 'expand',
        renderer: expandCollapseRenderer(this.switchExpandedRow.bind(this)),
        sortable: false,
      },
      { headerName: 'Id', field: 'id', sortable: true },
      { headerName: 'Event', field: 'event', sortable: true },
      { headerName: 'User', field: 'userEmail', sortable: true },
      { headerName: 'Date (UTC)', field: 'date', sortable: true },
      { headerName: 'SAP ID', field: 'sapId', sortable: true },
      { headerName: 'Success', field: 'success', renderer: SuccessRenderer, sortable: true },
    ];
  }

  public get availableTypes(): any[] {
    return [
      { value: 'UPGRADE_TASK', label: 'Upgrade task' },
      { value: 'USER_MANAGEMENT', label: 'User management' },
    ];
  }

  @action.bound
  public switchExpandedRow(row: AuditEvent): void {
    if (this.expandedRowIds.includes(row.id)) {
      this.expandedRowIds = this.expandedRowIds.filter(id => id !== row.id);
    } else {
      this.expandedRowIds = [...this.expandedRowIds, row.id];
    }
  }

  @action
  public async init(): Promise<void> {
    await this.fetchAuditEvents();
  }

  @action
  public close(): void {
    this.sorting = { ...initialSort };
    this.paging = { ...initialPaging };
  }

  @action
  // @ts-ignore
  public async fetchAuditEvents(): Promise<void> {
    runInAction(() => (this.loadingEvents = true));
    try {
      const response = await this.root.apiStore.callBackendApiJson(
        getPathWithQuery(AUDIT_PATH, {
          sapId: this.sapId,
          auditPageSize: this.paging.pageSize,
          auditOffset: this.paging.offset,
          auditSortField: this.sorting.field,
          auditSortDirection: this.sorting.order,
          auditSearchText: this.searchText,
          auditFailuresOnly: this.isFailuresOnly,
          auditMinId: this.minAuditId || undefined,
          auditTypes: this.selectedTypes,
        }),
      );
      for (const event of response.events) {
        event.date = dayjs(event.date).utcOffset(0).format('MM-D-YY HH:mm:ss');
      }
      runInAction(() => {
        this.events = response.events;
        this.paging.totalEventsCount = response.totalCount;
      });
    } catch {
      runInAction(() => (this.events = []));
    }
    runInAction(() => (this.loadingEvents = false));
  }

  @action
  public updatePagingOffset(pageNumber: number): any {
    this.paging.offset = this.paging.pageSize * pageNumber;
  }
}
